/**
 * The one moving thing in the console.
 *
 * The brief's motion budget is "120ms on hover/colour, nothing else" — no
 * page transitions, no skeleton shimmer, because operators watch these
 * screens for hours and anything that moves without meaning is a tax. A
 * spinner survives that rule only because it IS the meaning: it says a
 * request is still in flight, and a static glyph saying that is a lie the
 * moment the request finishes.
 *
 * `motion-reduce:animate-none` honours the OS setting; the element still
 * announces itself, so the information is not carried by the motion.
 */

export type SpinnerSize = 'sm' | 'md'

export interface SpinnerProps {
  size?: SpinnerSize
  /** What is loading, for a screen reader. Default is deliberately generic;
   * pass something specific ("Loading sessions") wherever you know it. */
  label?: string
}

const SIZE_CLASSES: Record<SpinnerSize, string> = {
  sm: 'h-3 w-3 border',
  md: 'h-4 w-4 border-2',
}

export function Spinner({ size = 'sm', label = 'Loading' }: SpinnerProps) {
  return (
    <span role="status" aria-label={label} className="inline-flex">
      {/* A ring with one transparent quadrant: `border-strong` is the ring,
          `border-t-transparent` is the gap that makes the rotation legible.
          `border-strong` clears 3:1 on every surface, so the spinner is a
          lawful non-text indicator wherever it lands. */}
      <span
        aria-hidden="true"
        className={`animate-spin rounded-full border-strong border-t-transparent motion-reduce:animate-none ${SIZE_CLASSES[size]}`}
      />
    </span>
  )
}
